import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useListState - Reusable hook to sync list page filters with URL search params.
 *
 * Preserves search, filters, and pagination state in the URL so that
 * navigating away (to a detail page) and back restores the exact view.
 *
 * @param {Object} config
 * @param {Object} config.defaults - Default values for each filter key.
 *   Keys with string defaults are treated as text filters.
 *   Keys with number defaults are treated as numeric filters.
 * @param {string[]} [config.debounceKeys=[]] - Keys that should be debounced (e.g., search fields).
 * @param {number} [config.debounceMs=500] - Debounce delay in milliseconds.
 * @param {boolean} [config.resetPageOnChange=true] - Reset page to 1 when any filter changes.
 * @param {string} [config.prefix=''] - Optional URL key prefix (e.g. 'b2b_') to namespace
 *   params when multiple independent list states share the same URL.
 *
 * @returns {Object} { params, setParam, setParams, resetAll, debouncedParams, page, setPage, currentUrl }
 */
export default function useListState({
  defaults = {},
  debounceKeys = [],
  debounceMs = 500,
  resetPageOnChange = true,
  prefix = '',
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialMount = useRef(true);
  const debounceTimers = useRef({});

  // Capture config in refs on first render so that inline object literals
  // passed by the consumer don't cause unnecessary re-renders.
  const defaultsRef = useRef(defaults);
  const debounceKeysRef = useRef(debounceKeys);
  const debounceMsRef = useRef(debounceMs);
  const resetPageOnChangeRef = useRef(resetPageOnChange);
  const prefixRef = useRef(prefix);

  // Parse current URL params into state, falling back to defaults.
  // Only depends on searchParams so it's only recreated when the URL changes.
  const getParamsFromURL = useCallback(() => {
    const result = {};
    const pfx = prefixRef.current;
    for (const [key, defaultVal] of Object.entries(defaultsRef.current)) {
      const urlVal = searchParams.get(pfx + key);
      if (urlVal === null || urlVal === undefined) {
        result[key] = defaultVal;
      } else if (typeof defaultVal === 'number') {
        result[key] = Number(urlVal);
      } else {
        result[key] = urlVal;
      }
    }
    return result;
  }, [searchParams]);

  // Current page from URL (namespaced by prefix)
  const page = useMemo(() => {
    const p = searchParams.get(`${prefixRef.current}page`);
    return p ? Number(p) : 1;
  }, [searchParams]);

  // All filter params (excluding page)
  const params = useMemo(() => getParamsFromURL(), [getParamsFromURL]);

  // Debounced values for search fields
  const [debouncedParams, setDebouncedParams] = useState(() => {
    const initial = {};
    for (const key of debounceKeysRef.current) {
      initial[key] = params[key] ?? defaultsRef.current[key] ?? '';
    }
    return initial;
  });

  // Sync debounced params when URL params change (for debounce keys only)
  useEffect(() => {
    if (isInitialMount.current) {
      // On initial mount, set debounced values immediately from URL
      const initial = {};
      for (const key of debounceKeysRef.current) {
        initial[key] = params[key] ?? defaultsRef.current[key] ?? '';
      }
      setDebouncedParams(initial);
      isInitialMount.current = false;
      return;
    }

    // Capture ref values inside the effect so cleanup uses the same snapshot
    const keys = debounceKeysRef.current;
    const timers = debounceTimers.current;

    // For subsequent changes, debounce
    for (const key of keys) {
      if (timers[key]) {
        clearTimeout(timers[key]);
      }
      timers[key] = setTimeout(() => {
        setDebouncedParams((prev) => ({
          ...prev,
          [key]: params[key] ?? defaultsRef.current[key] ?? '',
        }));
      }, debounceMsRef.current);
    }

    return () => {
      for (const key of keys) {
        if (timers[key]) {
          clearTimeout(timers[key]);
        }
      }
    };
  }, [params]); // Only depends on params (recreated only on real URL changes)

  // Set multiple params in one URL update (avoids multiple re-renders/API calls)
  const setParams = useCallback(
    (updates, options = {}) => {
      const { resetPage = resetPageOnChangeRef.current } = options;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const pfx = prefixRef.current;
          for (const [key, value] of Object.entries(updates)) {
            const defaultVal = defaultsRef.current[key];
            // For sortKey and sortBy, always pass them to URL if they have values
            // Don't delete them even if they match defaults
            if (
              (key === 'sortKey' || key === 'sortBy') &&
              value !== undefined &&
              value !== null &&
              value !== ''
            ) {
              next.set(pfx + key, String(value));
            } else if (
              value === defaultVal ||
              value === '' ||
              value === undefined ||
              value === null
            ) {
              next.delete(pfx + key);
            } else {
              next.set(pfx + key, String(value));
            }
          }
          if (resetPage) {
            next.delete(`${pfx}page`);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Set a single param and update URL
  const setParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const pfx = prefixRef.current;
          const defaultVal = defaultsRef.current[key];

          // Don't write default values to URL (keeps URL clean)
          if (
            value === defaultVal ||
            value === '' ||
            value === undefined ||
            value === null
          ) {
            next.delete(pfx + key);
          } else {
            next.set(pfx + key, String(value));
          }

          // Reset page when a filter changes
          if (resetPageOnChangeRef.current && key !== 'page') {
            next.delete(`${pfx}page`);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Set page
  const setPage = useCallback(
    (pageNum) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const pfx = prefixRef.current;
          if (pageNum <= 1) {
            next.delete(`${pfx}page`);
          } else {
            next.set(`${pfx}page`, String(pageNum));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset only this instance's filters and page (prefix-scoped)
  const resetAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const pfx = prefixRef.current;
        for (const key of Object.keys(defaultsRef.current)) {
          next.delete(pfx + key);
        }
        next.delete(`${pfx}page`);
        return next;
      },
      { replace: true },
    );
    // Reset debounced params immediately
    const cleared = {};
    for (const key of debounceKeysRef.current) {
      cleared[key] = defaultsRef.current[key] ?? '';
    }
    setDebouncedParams(cleared);
  }, [setSearchParams]);

  // Build the current query string for returnUrl usage
  const currentUrl = useMemo(() => {
    const qs = searchParams.toString();
    return qs ? `?${qs}` : '';
  }, [searchParams]);

  return {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    resetAll,
    currentUrl,
  };
}
